import * as React from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const tiers = [{
  id: "website",
  name: "Website Feature Video",
  description: "Transform your website into a premium experience with a cinematic motion video that automatically plays when visitors arrive, creating a powerful first impression and instantly elevating your brand.",
  rateLabel: "FROM",
  rate: "$2,500",
  isPopular: false,
  buttonText: "ELEVATE YOUR WEBSITE",
  buttonLink: "/contact"
}, {
  id: "commercial",
  name: "Commercial Video",
  description: "Professional AI-powered commercials designed to showcase products, services, and brands with cinematic quality—without the cost of traditional production.",
  rateLabel: "FROM",
  rate: "$3,000",
  isPopular: true,
  buttonText: "START PROJECT",
  buttonLink: "/contact"
}, {
  id: "instructional",
  name: "Instructional / Product Demo Video",
  description: "Perfect for explaining products, software, services, or processes with engaging educational content that builds customer confidence and drives conversions.",
  rateLabel: "FROM",
  rate: "$3,500",
  isPopular: false,
  buttonText: "CREATE DEMO",
  buttonLink: "/contact"
}];
const features = [{
  tierId: "website",
  text: "Up to 30 Seconds"
}, {
  tierId: "website",
  text: "AI Creative Direction"
}, {
  tierId: "website",
  text: "Cinematic Motion Design"
}, {
  tierId: "website",
  text: "Optimized for Web Performance"
}, {
  tierId: "website",
  text: "4K Delivery"
}, {
  tierId: "commercial",
  text: "Up to 30 Seconds"
}, {
  tierId: "commercial",
  text: "Creative Strategy & Storyboarding"
}, {
  tierId: "commercial",
  text: "AI Video Production"
}, {
  tierId: "commercial",
  text: "Professional Editing"
}, {
  tierId: "commercial",
  text: "Licensed Music"
}, {
  tierId: "commercial",
  text: "4K Delivery"
}, {
  tierId: "instructional",
  text: "Up to 60 Seconds"
}, {
  tierId: "instructional",
  text: "Script Development"
}, {
  tierId: "instructional",
  text: "AI Video Production"
}, {
  tierId: "instructional",
  text: "Professional Editing"
}, {
  tierId: "instructional",
  text: "Licensed Music"
}, {
  tierId: "instructional",
  text: "4K Delivery"
}];
export default function TailoredFinancialSuites() {
  return <section data-section-id="222449" id="production-access" className="bg-background text-foreground py-24 lg:py-32 relative overflow-hidden bg-[radial-gradient(circle_at_center,_hsl(270,100%,60%,0.1)_0%,_hsl(240,10%,3.9%)_100%)]">
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="mb-16 text-center max-w-3xl mx-auto">
          <h2 className="font-default text-4xl md:text-6xl font-bold tracking-tighter text-foreground mb-4 uppercase">
            Production Access
          </h2>
          <p className="font-sans text-lg text-muted-foreground">
            Select your tier of AI-powered cinematic production. High-gloss commercials designed to convert viewers into customers at the speed of light.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start max-w-6xl mx-auto">
          {tiers.map((tier, i) => {
          const tierFeatures = features.filter(f => f.tierId === tier.id);
          return <div data-index={i} key={tier.id} className={`relative flex flex-col ${tier.isPopular ? 'md:-mt-8 md:mb-8' : ''}`}>
                {tier.isPopular && <div className="absolute -top-4 left-0 right-0 flex justify-center z-20">
                    <span className="bg-gradient-to-r from-primary to-secondary text-primary-foreground text-xs font-bold uppercase tracking-widest py-1.5 px-6 rounded-lg shadow-lg font-default animate-pulse">
                      MOST POPULAR
                    </span>
                  </div>}
                
                <Card className={`flex-1 flex flex-col rounded-2xl border-2 h-full backdrop-blur-xl transition-all duration-500 ${tier.isPopular ? 'border-primary shadow-[0_0_40px_rgba(0,255,255,0.15)] scale-100 md:scale-105 z-10 bg-card/80 hover:-translate-y-2 hover:shadow-[0_0_30px_rgba(0,255,255,0.4)]' : 'border-border/50 shadow-sm bg-card/40 hover:bg-card/60 hover:border-muted-foreground/30 hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(0,255,255,0.4)]'}`}>
                  <CardHeader className="text-center pb-8 pt-10">
                    <CardTitle className="font-default text-2xl mb-2 text-foreground tracking-tight">
                      {tier.name}
                    </CardTitle>
                    <CardDescription className="font-sans text-sm min-h-[40px] text-muted-foreground leading-relaxed">
                      {tier.description}
                    </CardDescription>
                    <div className="mt-6">
                      <span className="block font-mono text-xs font-semibold text-primary mb-1 uppercase tracking-widest">
                        {tier.rateLabel}
                      </span>
                      <span className="font-default text-6xl font-bold tracking-tighter text-foreground">
                        {tier.rate}
                      </span>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="flex-1">
                    <div className="mb-4 text-xs font-mono text-muted-foreground uppercase tracking-widest border-b border-border/50 pb-2">
                      Neural Features
                    </div>
                    <ul className="space-y-4">
                      {tierFeatures.map((feature, j) => <li data-index={j} key={j} className="flex items-start gap-3 group">
                          <Check className="size-5 text-primary shrink-0 mt-0.5 drop-shadow-[0_0_5px_rgba(0,255,255,0.5)]" strokeWidth={3} />
                          <span className="font-sans text-sm text-foreground/90 group-hover:text-foreground transition-colors">
                            {feature.text}
                          </span>
                        </li>)}
                    </ul>
                  </CardContent>
                  
                  <CardFooter className="pt-8 pb-10">
                    <Button asChild variant={tier.isPopular ? "default" : "outline"} className={`w-full rounded-xl h-14 text-base font-bold uppercase tracking-widest transition-all duration-150 hover:scale-[1.03] hover:brightness-110 active:scale-[0.98] ${tier.isPopular ? 'bg-primary text-primary-foreground hover:shadow-[0_0_20px_rgba(0,255,255,0.4)]' : 'border-2 border-primary/50 text-primary hover:bg-primary/10'}`}>
                      <Link to={tier.buttonLink}>
                        {tier.buttonText}
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}